// CollectSEA Taxonomies (Mechanisms & Capabilities)
import { FailureMechanism, Capability } from './collectSeaTypes';

export const FAILURE_MECHANISMS: FailureMechanism[] = [
  {
    failure_mechanism_id: "SEA-FM-001",
    short_code: "AMBIGUOUS_RETRY_DUPLICATION",
    name: "Duplicate Consequential Action After Ambiguous Result",
    definition: "An automated process or agent triggers a duplicate state-changing or financial transaction because a network timeout or ambiguous external API response was falsely interpreted as a failure.",
    trigger_conditions: [
      "Dependency on external payment, messaging, or provisioning API",
      "Network connection times out or returns HTTP 504/502 while remote system actually processed request",
      "Automatic retry loop executes without idempotency key or explicit status reconciliation"
    ],
    possible_consequences: [
      "Double billing or duplicate disbursements",
      "Duplicate order fulfillment or ticket allocation",
      "Data corruption and ledger divergence"
    ],
    typical_observability: [
      "Multiple distinct transaction IDs associated with a single logical user intent",
      "High rate of client-side timeouts followed immediately by success responses",
      "Mismatch between internal order status and external payment gateway records"
    ],
    engineering_principles: [
      "Attach unique, client-generated idempotency keys to all state-mutating requests",
      "Treat timeout / network errors as UNKNOWN state rather than FAILURE",
      "Require an out-of-band status query or reconciliation before repeating any consequential mutation"
    ],
    related_incident_ids: [
      "SEA-INC-001",
      "SEA-INC-018",
      "SEA-INC-041"
    ],
    evidence_count: 3,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-002",
    short_code: "UNENFORCED_APPROVAL_BOUNDARY",
    name: "Privileged Action Without Enforceable Approval Boundary",
    definition: "An autonomous agent or automated workflow executes high-impact or policy-violating operations because safety checks relied on soft prompt guidelines rather than deterministic authorization gates.",
    trigger_conditions: [
      "Autonomous AI agent or script granted direct execution tokens",
      "Thresholds or approval requirements evaluated only via prompt instructions or client-side logic",
      "Absence of hard backend authorization interceptors enforcing human-in-the-loop sign-off"
    ],
    possible_consequences: [
      "Unauthorized refunds or fee waivers",
      "Legally binding commitments generated without executive approval",
      "Irreversible data deletion or permission escalations"
    ],
    typical_observability: [
      "High-value transactions executed without corresponding approval token or supervisor signature in audit log",
      "Prompt audit shows agent agreeing to out-of-policy requests",
      "Sudden surge in automated approval velocity"
    ],
    engineering_principles: [
      "Enforce business policy limits in deterministic code, never inside LLM prompts",
      "Implement cryptographically signed approval tokens for actions above defined risk thresholds",
      "Segregate tool interfaces: agents can propose mutations, but execution requires dual-control verification"
    ],
    related_incident_ids: [
      "SEA-INC-002",
      "SEA-INC-012",
      "SEA-INC-029"
    ],
    evidence_count: 3,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-003",
    short_code: "CROSS_TENANT_ID_SPOOFING",
    name: "Cross-Tenant Authorization Based on Caller-Controlled Identifier",
    definition: "A multi-tenant application or API endpoint retrieves or modifies sensitive customer records using a tenant or organization ID passed directly in the request body/URL without verifying against the authenticated session token.",
    trigger_conditions: [
      "Shared database or cache housing multiple customer tenants",
      "Queries filter by `tenant_id` supplied in request parameters rather than claims in verified JWT/session",
      "Lack of row-level security or tenant boundary middleware"
    ],
    possible_consequences: [
      "Cross-tenant data exposure (BOLA / IDOR)",
      "Unauthorized cross-tenant updates or deletions",
      "Severe regulatory breach (GDPR/HIPAA)"
    ],
    typical_observability: [
      "Mismatch between caller authenticated account ID and target resource tenant ID in access logs",
      "Sequential resource ID enumerations resulting in successful 200 OK responses",
      "Customer complaints regarding seeing foreign account names"
    ],
    engineering_principles: [
      "Extract tenant identity exclusively from cryptographically verified server-side session context",
      "Enforce database row-level security (RLS) or tenant-scoped query builders",
      "Run automated authorization regression tests verifying cross-tenant access rejection"
    ],
    related_incident_ids: [
      "SEA-INC-003",
      "SEA-INC-006",
      "SEA-INC-014",
      "SEA-INC-017"
    ],
    evidence_count: 4,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-004",
    short_code: "UNTRUSTED_CONTENT_TOOL_INJECTION",
    name: "Untrusted Content Changes AI Tool Behavior (Indirect Prompt Injection)",
    definition: "An AI agent reads external, user-supplied, or third-party content (e.g. emails, PDF invoices, web pages) containing adversarial instructions that override the system prompt and hijack tool execution.",
    trigger_conditions: [
      "AI model ingests untrusted text and has access to state-changing tools (APIs, email, database, shell)",
      "System lacks clear delimiter segregation and content security boundary between system instructions and data",
      "Autonomous tool execution without human confirmation on sensitive payloads"
    ],
    possible_consequences: [
      "Data exfiltration via outbound webhooks or email",
      "Execution of unintended transactions on behalf of the victim",
      "Privilege escalation and unauthorized state tampering"
    ],
    typical_observability: [
      "Agent logs showing prompt deviation immediately following retrieval of a specific external document",
      "Unexpected tool arguments containing encoded or synthesized command sequences",
      "Outbound network calls to unknown external domains triggered by agent"
    ],
    engineering_principles: [
      "Treat all document and external text as untrusted data; never interpolate directly into instruction channels",
      "Constrain tool permissions via strict principle of least privilege (e.g., read-only tokens for research agents)",
      "Require explicit user confirmation for any tool call with external side-effects"
    ],
    related_incident_ids: [
      "SEA-INC-007",
      "SEA-INC-008",
      "SEA-INC-020",
      "SEA-INC-048"
    ],
    evidence_count: 4,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-005",
    short_code: "UNVERIFIED_DESTRUCTIVE_RECOVERY",
    name: "Destructive Operation Without Verified Recovery",
    definition: "Automated cleanup scripts, administrative commands, or AI maintenance agents delete data or infrastructure assets while backup restoration procedures are untested, stale, or inaccessible.",
    trigger_conditions: [
      "Automated scripts or agents with deletion permissions (`DELETE`, `DROP`, `rm -rf`, cloud deprovisioning)",
      "Ambiguous filter logic or missing environment safeguards (e.g. running prod deletion script during test)",
      "Backups exist only as unverified snapshots without regular automated restore drills"
    ],
    possible_consequences: [
      "Permanent data loss and prolonged downtime",
      "Inability to recover production state",
      "Catastrophic business continuity failure"
    ],
    typical_observability: [
      "Sudden mass deletion events logged in cloud audit trails",
      "Backup restoration processes failing on schema mismatch or missing snapshot files",
      "Total absence of soft-delete or retention grace periods"
    ],
    engineering_principles: [
      "Implement soft-deletes and mandatory time-delayed retention windows for all destructive operations",
      "Require multi-factor out-of-band authorization for bulk resource termination",
      "Conduct continuous automated backup restore verification to prove recovery point and time objectives"
    ],
    related_incident_ids: [
      "SEA-INC-005",
      "SEA-INC-035"
    ],
    evidence_count: 2,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-006",
    short_code: "RETRY_AMPLIFICATION_RUNAWAY",
    name: "Retry Amplification / Runaway Resource Consumption",
    definition: "When a backend or downstream dependency slows down or fails, upstream clients or automated agents aggressively retry without backoff or jitter, creating a self-inflicted denial-of-service avalanche.",
    trigger_conditions: [
      "Synchronous retry loops configured without exponential backoff and random jitter",
      "Agents looping on parsing failures or validation errors without cycle limits",
      "Absence of circuit breakers and distributed rate limiters"
    ],
    possible_consequences: [
      "Cascading service failure across the entire infrastructure",
      "Massive cloud / LLM token billing spikes",
      "Total outage lasting hours as queues overwhelm downstream databases"
    ],
    typical_observability: [
      "Exponential request rate spike coinciding with rising downstream latency",
      "Thread pool exhaustion and high memory pressure on API gateways",
      "Single agent conversation generating thousands of rapid API requests in minutes"
    ],
    engineering_principles: [
      "Always apply exponential backoff with full jitter to all retry attempts",
      "Implement circuit breakers to fast-fail when downstream error rates exceed thresholds",
      "Enforce hard per-task token, iteration, and execution time budget caps"
    ],
    related_incident_ids: [
      "SEA-INC-004",
      "SEA-INC-021",
      "SEA-INC-042"
    ],
    evidence_count: 3,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-007",
    short_code: "PARTIAL_SUCCESS_STATE_DIVERGENCE",
    name: "External Dependency Partial Success Creates State Divergence",
    definition: "A composite workflow updates local database records or external services, but a failure on an intermediate step leaves internal state out of sync with external reality.",
    trigger_conditions: [
      "Multi-step transaction spanning non-transactional resources (e.g. database + external Stripe API + email)",
      "Lack of two-phase commit, transactional outbox pattern, or saga orchestrator",
      "Failure handling fails to execute compensatory rollback operations"
    ],
    possible_consequences: [
      "Customer charged but account status not upgraded",
      "Inventory deducted without confirmed payment",
      "Orphaned background processes and ghost records"
    ],
    typical_observability: [
      "Audit trail discrepancies between external webhook events and internal entity status",
      "Dangling records with `PENDING` status exceeding timeout SLAs",
      "Customer support tickets reporting missing access after successful receipts"
    ],
    engineering_principles: [
      "Use transactional outbox pattern or idempotent saga workflows for multi-service operations",
      "Make state transitions event-driven based on verified external webhook notifications",
      "Implement periodic background reconciliation jobs to self-heal orphaned states"
    ],
    related_incident_ids: [
      "SEA-INC-018",
      "SEA-INC-034",
      "SEA-INC-046"
    ],
    evidence_count: 3,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-008",
    short_code: "STALE_AUTHORIZATION_ACTUATION",
    name: "Automation Acts on Stale Authorization or Outdated State",
    definition: "An automated service, scheduled worker, or agent executes actions based on cached entitlements or resource states that were revoked or changed since the task was queued.",
    trigger_conditions: [
      "Long-running asynchronous jobs or queued tasks",
      "Permissions or resource status verified only at job creation time rather than at execution time",
      "Long TTL in distributed caching layer without active invalidation push"
    ],
    possible_consequences: [
      "Executing actions for cancelled subscriptions or offboarded employees",
      "Modifying resources that were already locked, transferred, or deleted",
      "Security compliance and authorization violations"
    ],
    typical_observability: [
      "Mutations executed by workers referencing user accounts marked as `DELETED` or `SUSPENDED`",
      "Cache hit metrics showing 100% hits during user permission revocation events",
      "Late-arriving webhooks applying state on stale object revisions"
    ],
    engineering_principles: [
      "Verify authorization and optimistic locking version tags immediately before executing state mutations",
      "Include active cancellation tokens checked by async workers prior to applying side effects",
      "Maintain short cache TTLs with event-driven cache purge on entitlement changes"
    ],
    related_incident_ids: [
      "SEA-INC-019",
      "SEA-INC-036"
    ],
    evidence_count: 2,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-009",
    short_code: "INCOMPLETE_AUDIT_PROVENANCE",
    name: "Audit Trail Omits Actor, Target, or Decision Context",
    definition: "Security or operational logs record that an action occurred but omit critical provenance details (e.g. initiating user identity, agent prompt, input source, or evaluation rationale), making forensic attribution impossible.",
    trigger_conditions: [
      "Log statements structured around simple string prints rather than standardized schemas",
      "Autonomous agent or background job acting under a generic shared service account",
      "Failure to log input prompts, model versions, and tool arguments for consequential actions"
    ],
    possible_consequences: [
      "Inability to detect source of breach or fraudulent transactions",
      "Failed compliance audits under SOC 2, HIPAA, or ISO 27001",
      "Uncertainty during incident response leading to over-broad remediation shutdowns"
    ],
    typical_observability: [
      "Audit logs showing actions performed by `system` with null `initiator_id` and missing metadata",
      "Inability to trace a specific database row mutation back to a user HTTP request",
      "Disjointed trace IDs across async worker boundaries"
    ],
    engineering_principles: [
      "Propagate structured distributed correlation IDs and human/agent principal metadata through every hop",
      "Log immutable structured audit events containing actor, action, target entity, previous state, new state, and reason",
      "Store full AI decision context (prompt hash, model version, tool call arguments, output) in tamper-evident logs"
    ],
    related_incident_ids: [
      "SEA-INC-011",
      "SEA-INC-031"
    ],
    evidence_count: 2,
    status: "canonical"
  },
  {
    failure_mechanism_id: "SEA-FM-010",
    short_code: "TOOL_HALLUCINATION_SCOPE_EXCEED",
    name: "AI Action Exceeds Intended Scope / Tool Hallucination",
    definition: "An LLM-driven agent invokes tools with fabricated parameters, hallucinated identifiers, or destructive arguments that exceed the user's explicit intent.",
    trigger_conditions: [
      "Broad, unconstrained tool function signatures provided to LLM",
      "Model hallucinates resource IDs, SQL queries, or filter parameters",
      "System executes model tool calls directly without parameter validation against existing session state"
    ],
    possible_consequences: [
      "Accidental modification of unrelated customer records",
      "Execution of destructive drop/delete commands",
      "Corrupted foreign key references and data pollution"
    ],
    typical_observability: [
      "Database foreign key violation spikes caused by agent passing non-existent IDs",
      "Tool invocations containing valid syntax but impossible semantic parameters",
      "User confusion when agent confirms completion of an action that was applied to wrong target"
    ],
    engineering_principles: [
      "Constrain tool parameter types using strict schemas (e.g. enums, verified ID lists) rather than raw free-text",
      "Validate that all resource IDs referenced in tool calls belong to the active user's authorized scope",
      "Provide dry-run preview capabilities for high-impact tool invocations"
    ],
    related_incident_ids: [
      "SEA-INC-002",
      "SEA-INC-007",
      "SEA-INC-037"
    ],
    evidence_count: 3,
    status: "canonical"
  }
];

export const MECHANISMS = FAILURE_MECHANISMS;

export const MECHANISM_MAP = new Map<string, FailureMechanism>(
  FAILURE_MECHANISMS.map(m => [m.failure_mechanism_id, m])
);

export const CAPABILITY_VOCABULARY_VERSION = "2026.1";

export const CAPABILITIES: Capability[] = [
  // AUTHORITY
  {
    id: "auth.read_only",
    label: "Read-Only Automation",
    category: "authority",
    description: "System reads data or environment state without executing state-modifying actions.",
    confidence: 1.0
  },
  {
    id: "auth.write_action",
    label: "Write / State Modification",
    category: "authority",
    description: "System executes mutations, record writes, or file updates in databases or storage.",
    confidence: 1.0
  },
  {
    id: "auth.financial_action",
    label: "Financial Consequential Action",
    category: "authority",
    description: "System executes refunds, payments, credits, wire transfers, or monetary changes.",
    confidence: 1.0
  },
  {
    id: "auth.privileged_action",
    label: "Privileged Execution / Admin Action",
    category: "authority",
    description: "System operates with administrative, security, or elevated permissions.",
    confidence: 1.0
  },
  {
    id: "auth.destructive_action",
    label: "Destructive Action / Deletion",
    category: "authority",
    description: "System deletes databases, records, storage resources, or infrastructure assets.",
    confidence: 1.0
  },
  {
    id: "auth.account_entitlement",
    label: "Account / Role / Entitlement Modification",
    category: "authority",
    description: "System provisions, modifies, or revokes user roles, permissions, or access tokens.",
    confidence: 1.0
  },
  {
    id: "auth.external_communication",
    label: "External Customer Communication",
    category: "authority",
    description: "System sends automated outbound emails, SMS, webhooks, or chat messages to customers.",
    confidence: 1.0
  },
  {
    id: "auth.public_publication",
    label: "Public Publication",
    category: "authority",
    description: "System publishes content, social media posts, public web pages, or public documentation.",
    confidence: 1.0
  },
  {
    id: "auth.infrastructure_modification",
    label: "Infrastructure Modification",
    category: "authority",
    description: "System creates, scales, modifies, or provisions cloud compute, DNS, or networking.",
    confidence: 1.0
  },
  {
    id: "auth.code_execution",
    label: "Dynamic Code / Shell Execution",
    category: "authority",
    description: "System executes dynamically generated code, shell commands, or containerized scripts.",
    confidence: 1.0
  },
  {
    id: "auth.autonomous_approval",
    label: "Autonomous Approval / Policy Decision",
    category: "authority",
    description: "System autonomously decides eligibility, approvals, discounts, or compliance without human gate.",
    confidence: 1.0
  },

  // DATA
  {
    id: "data.pii",
    label: "Sensitive Personal Information (PII)",
    category: "data",
    description: "System processes employee, customer, patient, or user personally identifiable data.",
    confidence: 1.0
  },
  {
    id: "data.credentials_secrets",
    label: "Credentials, Secrets & API Keys",
    category: "data",
    description: "System accesses, manages, or stores private API tokens, passwords, or encryption keys.",
    confidence: 1.0
  },
  {
    id: "data.uploaded_files",
    label: "Untrusted Uploaded Files / Documents",
    category: "data",
    description: "System accepts, parses, or processes PDFs, receipts, images, or files from users.",
    confidence: 1.0
  },
  {
    id: "data.untrusted_external_text",
    label: "Untrusted External Text / Emails / Prompts",
    category: "data",
    description: "System processes arbitrary text from user inputs, email bodies, public web pages, or chats.",
    confidence: 1.0
  },
  {
    id: "data.regulated_records",
    label: "Regulated Health / Compliance Records",
    category: "data",
    description: "System processes HIPAA, GDPR, PCI-DSS, or financial regulatory compliance records.",
    confidence: 1.0
  },
  {
    id: "data.financial_records",
    label: "Financial & Billing Records",
    category: "data",
    description: "System manages invoices, transaction histories, credit cards, or expense logs.",
    confidence: 1.0
  },
  {
    id: "data.cross_tenant_data",
    label: "Multi-Tenant Shared Data Store",
    category: "data",
    description: "System stores records belonging to distinct customer organizations in a shared pool.",
    confidence: 1.0
  },

  // ARCHITECTURE
  {
    id: "arch.multi_tenant",
    label: "Multi-Tenant SaaS Architecture",
    category: "architecture",
    description: "Application serves multiple distinct organizations with logical isolation boundaries.",
    confidence: 1.0
  },
  {
    id: "arch.external_api",
    label: "External Third-Party API Dependency",
    category: "architecture",
    description: "System depends on external SaaS/payment/vendor HTTP APIs with uncertain latency or timeouts.",
    confidence: 1.0
  },
  {
    id: "arch.async_job",
    label: "Asynchronous Background Processing / Queues",
    category: "architecture",
    description: "Work is decoupled into background queues, workers, batch jobs, or event streams.",
    confidence: 1.0
  },
  {
    id: "arch.event_driven",
    label: "Event-Driven Triggers / Webhooks",
    category: "architecture",
    description: "Actions are triggered automatically upon receiving external webhooks or internal events.",
    confidence: 1.0
  },
  {
    id: "arch.caching",
    label: "Shared Caching Layer (Redis / Memcached)",
    category: "architecture",
    description: "System caches permissions, tokens, or queries in an in-memory shared store.",
    confidence: 1.0
  },
  {
    id: "arch.distributed_tx",
    label: "Distributed State & Transactions",
    category: "architecture",
    description: "State must be coordinated across multiple microservices or external providers.",
    confidence: 1.0
  },
  {
    id: "arch.persistent_memory",
    label: "Persistent Agent State / Memory",
    category: "architecture",
    description: "AI agent maintains conversational history, vector embeddings, or scratchpads across turns.",
    confidence: 1.0
  },
  {
    id: "arch.tool_enabled_agent",
    label: "Tool-Enabled AI Agent / Function Calling",
    category: "architecture",
    description: "LLM autonomously selects and executes external tools, SQL queries, or APIs based on prompt.",
    confidence: 1.0
  },
  {
    id: "arch.content_ingestion_rag",
    label: "RAG / External Content Ingestion",
    category: "architecture",
    description: "System crawls, embeds, and retrieves external documentation or knowledge bases.",
    confidence: 1.0
  },

  // FAILURE / CONSEQUENCE SHAPE
  {
    id: "fail.duplicate_action",
    label: "Duplicate Consequential Execution",
    category: "failure_shape",
    description: "Risk of repeated financial transactions, duplicate messages, or redundant API mutations.",
    confidence: 1.0
  },
  {
    id: "fail.partial_success",
    label: "Partial Success / State Desynchronization",
    category: "failure_shape",
    description: "System updates one component but fails downstream, leaving records inconsistent.",
    confidence: 1.0
  },
  {
    id: "fail.ambiguous_external_result",
    label: "Ambiguous External Service Result / Timeout",
    category: "failure_shape",
    description: "External API drops connection or times out after accepting or completing the payload.",
    confidence: 1.0
  },
  {
    id: "fail.stale_state",
    label: "Stale Authorization / Cache Invalidation Gap",
    category: "failure_shape",
    description: "System acts upon cached entitlements or outdated resource status that has been revoked.",
    confidence: 1.0
  },
  {
    id: "fail.auth_boundary_failure",
    label: "Authorization Boundary Bypass / BOLA / IDOR",
    category: "failure_shape",
    description: "Caller accesses or modifies another user's resources via parameter manipulation.",
    confidence: 1.0
  },
  {
    id: "fail.cross_tenant_leak",
    label: "Cross-Tenant Data Exposure",
    category: "failure_shape",
    description: "Information belonging to Tenant A is returned or visible to Tenant B.",
    confidence: 1.0
  },
  {
    id: "fail.runaway_loop",
    label: "Runaway Execution Loop / Infinite Recursion",
    category: "failure_shape",
    description: "Automated agent or workflow enters an uncontrolled loop consuming credits or flooding APIs.",
    confidence: 1.0
  },
  {
    id: "fail.cost_resource_exhaustion",
    label: "Cost / Resource Exhaustion",
    category: "failure_shape",
    description: "Uncapped token usage, bandwidth, or infrastructure provisioning leads to runaway bills.",
    confidence: 1.0
  },
  {
    id: "fail.irreversible_change",
    label: "Irreversible Downstream Effect",
    category: "failure_shape",
    description: "Action cannot be undone via simple rollback or compensatory transaction.",
    confidence: 1.0
  },
  {
    id: "fail.recovery_failure",
    label: "Backup / Disaster Recovery Failure",
    category: "failure_shape",
    description: "Post-incident restoration fails due to untested snapshots or missing permissions.",
    confidence: 1.0
  },
  {
    id: "fail.missing_observability",
    label: "Missing Audit Trail / Opaque AI Decisions",
    category: "failure_shape",
    description: "Logs lack exact prompt context, caller identity, or reason code for consequential action.",
    confidence: 1.0
  },
  {
    id: "fail.unsafe_fallback",
    label: "Unsafe Fail-Open Fallback",
    category: "failure_shape",
    description: "When an external auth or safety filter fails, system silently defaults to permissive access.",
    confidence: 1.0
  },
  {
    id: "fail.cascading_dep_failure",
    label: "Cascading Synchronous Dependency Failure",
    category: "failure_shape",
    description: "Latency in a single downstream dependency exhausts worker thread pool and causes total outage.",
    confidence: 1.0
  }
];

export const CAPABILITY_MAP = new Map<string, Capability>(
  CAPABILITIES.map(c => [c.id, c])
);
